/**
 * 鉤子
 * 授權 - 權杖
 * @author J
 * @since 2024-04-30 07:34:25
 */

import {computed} from "vue";
import {AuthorizationSessionStore} from "@/v2/store/modules/authorization/session";
import {PermissionCollectionEnum} from "@/v2/enumerate/permission";
import {RouteNameEnum} from "@/v2/enumerate/route";
import {useRoute} from "vue-router";

/**
 * 實例
 */
export const useAuthorizationSession = () => {

    /**
     * 授權 - 權杖庫
     */
    const authorizationSessionStore = AuthorizationSessionStore()

    /**
     * 獲取 - uuid
     */
    const getUuid = computed(() => authorizationSessionStore.getUuid)

    /**
     * 用戶與管理員是否對應
     * 是否二維碼綁定過
     */
    const isRoot = computed(() => authorizationSessionStore.isRoot)

    /**
     * 用戶與管理員是否對應
     * 是否二維碼綁定過
     */
    const isMapping = computed(() => authorizationSessionStore.isMapping)

    /**
     * 獲取 - 帳號
     */
    const getAccount = computed(() => authorizationSessionStore.getAccount)

    /**
     * 獲取 - 名字
     */
    const getName = computed(() => authorizationSessionStore.getName)

    /**
     * 獲取 - 頭像
     */
    const getAvatarUrl = computed(() => authorizationSessionStore.getAvatarUrl)

    /**
     * 獲取 - 郵件
     */
    const getEmail = computed(() => authorizationSessionStore.getEmail)

    /**
     * 逾期時間
     */
    const getExpireAt = computed(() => authorizationSessionStore.getExpireAt)

    /**
     * 獲取 - 權限表
     */
    const getPermission = computed(() => authorizationSessionStore.getPermission)

    /**
     * 獲取 - 教會
     */
    const getChurch = computed(() => authorizationSessionStore.getChurch)

    /**
     * 獲取 - 軟體
     */
    const getSoftware = computed(() => authorizationSessionStore.getSoftware)

    /**
     * 獲取 - 軟體
     */
    const getValidator = computed(() => authorizationSessionStore.getValidator)

    /**
     * 總教會登入
     */
    const isHead = computed(() => authorizationSessionStore.getChurch.head);

    /**
     * 限制總教會僅讀取
     */
    const isHeadRestraint = computed(() => isHead.value && ((useRoute().meta).head?.readonly ?? false));

    /**
     * 檢查 - 權限
     * ============================================
     * 用 router.group + permissionCollection
     * 進行權限判斷
     * ============================================
     */
    const isHasPermission = computed(() => (
        permissionCollection
    ) => {

        const permission = (useRoute().meta).group ?? 0

        //取出組別
        const group = getPermission.value
            .find(i=>i.group === permission)

        //組別不存在, 返回沒權限
        if (group === undefined) {
            return false
        }

        //二進制權限判斷
        return (group.value & permissionCollection) > 0

    })

    //
    // /**
    //  * 解藕 - 二進制權限 - 累加
    //  * 解成 [LIST, STORE....]
    //  * @param permission 權限累加值
    //  */
    // const getPermissionCollection = computed((): (
    //     permission: number
    // ) => PermissionCollectionEnum[] => {
    //
    //     return (
    //         permission
    //     ) => {
    //
    //         return Object.values(PermissionCollectionEnum)
    //             .filter(j => typeof j === "number")
    //             .map(j => j as PermissionCollectionEnum)
    //             .reduce((a: PermissionCollectionEnum[], b: PermissionCollectionEnum) => {
    //                 if ((permission & b) > 0) a.push(b)
    //                 return a
    //             }, [])
    //
    //     }
    //
    // })
    //
    // /**
    //  * 解藕 - 二進制權限 - 累加
    //  * 具有幾個權限 [LIST, STORE] => 2
    //  * @param permission 權限累加值
    //  */
    // const getPermissionCollectionLength = computed((): (
    //     permission: number
    // ) => number => {
    //
    //     return (
    //         permission
    //     ) => {
    //
    //         let count = 0;
    //         while (permission > 0) {
    //             if ((permission & 1) > 0) {
    //                 count++;
    //             }
    //             permission >>= 1;
    //         }
    //         return count;
    //
    //     }
    //
    // })

    /**
     * 檢查 - 路由 - 權限
     * =============================
     * 以router.name檢查是否有權限
     * 檢查通過會給回到原始的路由名稱
     * 基本上是給左側選單開合用的
     * =============================
     * @param permission 權限 (預設是LIST)
     * @param name 路由名稱
     */
    const isHasRoutePermission = computed(() => (
        name,
        permission
    ) => {

        //服事
        if (name === RouteNameEnum.ADMIN_EVENT && isHasPermission.value(
            permission ?? PermissionCollectionEnum.LIST
        )) {
            return name
        }

        //二進制權限判斷
        return null

    })

    /**
     * 更新 - 安全性綁定
     */
    const setMapping = (
        mapping
    ) => authorizationSessionStore.setMapping(mapping)

    /**
     * 更新 - 名稱
     */
    const setName = (
        name
    ) => authorizationSessionStore.setName(name)

    /**
     * 更新 - 名稱
     */
    const setEmail = (
        email
    ) => authorizationSessionStore.setEmail(email)

    /**
     * 更新 - 頭像
     */
    const setAvatarUrl = (
        avatar
    ) => authorizationSessionStore.setAvatarUrl(avatar)

    /**
     * 更新 - 權限
     */
    const setPermission = (
        permission
    ) => authorizationSessionStore.setPermission(permission)

    /**
     * 更新 - 全部
     */
    const setData = (
        data
    ) => authorizationSessionStore.setData(data)

    return {
        getUuid,
        isRoot,
        isHead,
        isMapping,
        isHasPermission,
        isHasRoutePermission,
        isHeadRestraint,
        getAccount,
        getName,
        getAvatarUrl,
        getEmail,
        getExpireAt,
        getPermission,
        getChurch,
        getSoftware,
        getValidator,
        setMapping,
        setName,
        setEmail,
        setAvatarUrl,
        setPermission,
        setData
    }

}